/**
 * Accesos → Grupos, the list (Plan J, J3 — mockup 4, list half).
 *
 * A *grupo* carries an ámbito (the cut of the data) and a content matrix
 * (which reports and sections). A person's access is the OR of their
 * groups; a person in no group has none.
 *
 * WHO CREATES GROUPS
 * ------------------
 * PervasiveMind, here. Pablo, 2026-09-07: the client's own admin does
 * not create groups — they assign people to existing ones, and that
 * screen is J5, in the portal. So there is no import of this UI
 * anywhere else and no "who may create" toggle.
 *
 * THE STATE THIS LIST EXISTS TO SHOUT
 * -----------------------------------
 * `grants_nothing` — not one `(dashboard, view, granted)` row. Whoever
 * joins that group opens NO report at all, and in `SELECT * FROM
 * groups` it looks perfect. `health` is the worst-of word and the two
 * counters travel beside it, so a group that is both empty and grants
 * nothing is not silently reduced to one of the two.
 *
 * ROLE DEFAULTS LIVE HERE TOO
 * ---------------------------
 * `permission_role_defaults` is the layer BENEATH the group rows: it is
 * what an empty cell of the Contenido matrix MEANS. It is not a person
 * and not an ámbito, so Personas and Ámbitos are both the wrong home. It
 * is the second tab of this screen, which keeps the mockups' four
 * routes and puts the explanation next to the thing it explains.
 */

import { Button, Tabs, type TabItem } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  DateCell,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  StatusChip,
  type Column,
  type RowAction,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Eye, Plus, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useT } from "@/i18n";
import {
  SCOPE_ALL,
  groupsApi,
  type GroupSummaryOut,
} from "@/lib/access-api";
import { DictChip } from "@/pages/admin-access/AccessCells";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import { groupHealthDict } from "@/pages/admin-access/access-status";
import { GroupCreateModal } from "@/pages/admin-access/GroupCreateModal";
import { GroupDeleteModal } from "@/pages/admin-access/GroupDeleteModal";
import { RoleDefaultsPane } from "@/pages/admin-access/RoleDefaultsPane";

type GroupsTab = "groups" | "defaults";

export function AccessGroupsPage(): JSX.Element {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: GroupsTab =
    searchParams.get("tab") === "defaults" ? "defaults" : "groups";

  const [rows, setRows] = useState<GroupSummaryOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<GroupSummaryOut | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setRows((await groupsApi.list()).groups);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const health = useMemo(() => groupHealthDict(t), [t]);

  const columns: Column<GroupSummaryOut>[] = [
    {
      id: "group",
      header: t("access.groups.col.group"),
      cell: (row) => (
        // Bounded width: in an auto-layout table a one-line `truncate`
        // made this column as wide as the longest description and pushed
        // the other five columns out of view. Two lines, then ellipsis.
        <div className="min-w-0 max-w-md">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium text-neutral-900">
              {row.name}
            </span>
            {row.is_base && (
              <StatusChip
                label={t("access.groups.base.chip")}
                tone="info"
                title={t("access.groups.base.chipTitle")}
              />
            )}
          </div>
          {row.description !== "" && (
            <div className="line-clamp-2 text-[13px] text-neutral-500">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "scope",
      header: t("access.groups.col.scope"),
      width: "w-52",
      cell: (row) => (
        <StatusChip
          // `all` is displayed as "Todo" — the reserved scope's own
          // display name. The GROUP called "Todos" is a different object
          // and lives in the column to the left; keeping the two words
          // distinct is why the baseline group is not named "Todo".
          label={row.scope_id === SCOPE_ALL ? t("access.scopes.all") : row.scope_name}
          tone={row.scope_id === SCOPE_ALL ? "warning" : "info"}
          title={
            row.scope_id === SCOPE_ALL
              ? t("access.groups.scopeAllHint")
              : row.scope_id
          }
        />
      ),
    },
    {
      id: "members",
      header: t("access.groups.col.members"),
      align: "right",
      width: "w-28",
      cell: (row) => <span className="text-neutral-900">{row.member_count}</span>,
    },
    {
      id: "content",
      header: t("access.groups.col.content"),
      width: "w-52",
      cell: (row) => (
        <span className="text-[13px] text-neutral-700">
          {t("access.groups.contentCount", {
            reports: row.entitled_report_count,
            projects: row.project_permission_count,
          })}
        </span>
      ),
    },
    {
      id: "health",
      header: t("access.groups.col.health"),
      width: "w-44",
      cell: (row) => (
        <DictChip
          value={row.health}
          dictionary={health}
          t={t}
          title={row.health_detail}
        />
      ),
    },
    {
      id: "updated",
      header: t("access.groups.col.updated"),
      width: "w-40",
      cell: (row) => (
        <DateCell
          value={row.updated_at}
          locale={locale}
          mode="absolute"
          invalidHint={t("access.common.badDate")}
        />
      ),
    },
  ];

  const actions: RowAction<GroupSummaryOut>[] = [
    {
      id: "detail",
      label: t("access.groups.actions.detail"),
      icon: <Eye size={14} />,
      onSelect: (row) =>
        navigate(`/admin/access/groups/${encodeURIComponent(row.id)}`),
    },
    {
      id: "delete",
      label: t("access.groups.actions.delete"),
      icon: <Trash2 size={14} />,
      danger: true,
      // The base group is never deleted (D-G4); the server answers 409.
      hidden: (row) => row.is_base,
      onSelect: (row) => setDeleting(row),
    },
  ];

  const tabs: TabItem[] = [
    { id: "groups", label: t("access.groups.tab.groups") },
    { id: "defaults", label: t("access.groups.tab.defaults") },
  ];

  return (
    <PageContainer wide>
      <PageHeader
        title={t("access.groups.title")}
        subtitle={t("access.groups.subtitle")}
        actions={
          tab === "groups" ? (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setCreating(true)}
            >
              {t("access.groups.add")}
            </Button>
          ) : undefined
        }
      />
      <AccessTabs active="groups" />

      <div className="mb-4 border-b border-neutral-200">
        <Tabs
          items={tabs}
          activeId={tab}
          onChange={(id: string) =>
            setSearchParams(id === "groups" ? {} : { tab: id }, {
              replace: true,
            })
          }
          variant="underline"
          size="sm"
        />
      </div>

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      {tab === "defaults" ? (
        <RoleDefaultsPane />
      ) : (
        <Section flush>
          {loading ? (
            <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-neutral-500">
              <Spinner size={16} ariaLabel={t("common.loading")} />
              {t("common.loading")}
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              actions={actions}
              caption={t("access.groups.title")}
              onRowClick={(row) =>
                navigate(`/admin/access/groups/${encodeURIComponent(row.id)}`)
              }
              empty={
                <EmptyState
                  icon={<Users size={24} />}
                  title={t("access.groups.empty.title")}
                  description={t("access.groups.empty.desc")}
                  action={
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Plus size={14} />}
                      onClick={() => setCreating(true)}
                    >
                      {t("access.groups.add")}
                    </Button>
                  }
                />
              }
            />
          )}
        </Section>
      )}

      <GroupCreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          navigate(`/admin/access/groups/${encodeURIComponent(id)}`);
        }}
      />

      <GroupDeleteModal
        group={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          void refresh();
        }}
      />
    </PageContainer>
  );
}
